import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import axios from "axios";
import { Loader2, Key, User, Mail, UserPlus, X, Phone, Camera, Save, Fingerprint, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { startRegistration } from "@simplewebauthn/browser";

export function resolveAvatarSrc(profilePicture) {
  if (!profilePicture) return null;
  if (profilePicture.startsWith("http")) return profilePicture;
  const token = localStorage.getItem("token");
  const sep = profilePicture.includes("?") ? "&" : "?";
  return `${process.env.REACT_APP_BACKEND_URL}${profilePicture}${sep}auth=${token}`;
}

const ROLE_BADGE = {
  admin: "bg-[#1C1D20] text-[#F3CF5B] dark:bg-[#F3CF5B] dark:text-[#1C1D20]",
  finance_manager: "bg-[#172545] text-[#8FB0FF] dark:bg-[var(--dm-input-border)] dark:text-[#93C5FD]",
  account_manager: "bg-[#12301F] text-[#6FD39B] dark:bg-[#064E3B] dark:text-[#6EE7B7]",
};

const inputCls = "w-full h-10 px-3 rounded-lg border border-[#E5E5E5] dark:border-[var(--dm-input-border)] bg-white dark:bg-[var(--dm-input-bg)] text-sm text-[#1C1D20] dark:text-[var(--dm-text)] focus:outline-none focus:ring-2 focus:ring-[#F3CF5B]/50 focus:border-[#F3CF5B]";
const labelCls = "block text-xs font-medium text-[#1C1D20]/60 dark:text-[var(--dm-muted-text)] uppercase tracking-wider mb-1.5";
const primaryBtnCls = "flex items-center justify-center gap-2 px-5 h-10 text-sm font-semibold rounded-xl transition-colors disabled:opacity-60 bg-[#F3CF5B] hover:bg-[#E5BF47] text-[#1C1D20]";

export default function ProfilePage() {
  const { user, API, authHeader, refreshUser } = useAuth();
  const { t, lang } = useLang();

  const [activeTab, setActiveTab] = useState("personal");

  // Profile fields
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileMobile, setProfileMobile] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  // Password
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Delegation
  const [users, setUsers] = useState([]);
  const [delegateId, setDelegateId] = useState("");
  const [delegating, setDelegating] = useState(false);

  // Passkeys
  const [passkeys, setPasskeys] = useState([]);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);

  const canDelegate = (user?.permissions || []).includes("use_delegation");

  useEffect(() => {
    if (user) {
      setProfileName(user.name || "");
      setProfileEmail(user.email || "");
      setProfileMobile(user.mobile || "");
    }
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/users`, { headers: authHeader() })
      .then((res) => setUsers(res.data.filter((u2) => u2.id !== user?.id && u2.is_active)))
      .catch(() => {});
    axios.get(`${API}/auth/passkey/list`, { headers: authHeader() })
      .then((res) => setPasskeys(res.data))
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleAddPasskey = useCallback(async () => {
    setRegisteringPasskey(true);
    try {
      const optionsRes = await axios.post(`${API}/auth/passkey/register-options`, {}, { headers: authHeader() });
      const registration = await startRegistration({ optionsJSON: optionsRes.data });
      const verifyRes = await axios.post(`${API}/auth/passkey/register-verify`, registration, { headers: authHeader() });
      setPasskeys((prev) => [...prev, { id: verifyRes.data.passkey_id, name: verifyRes.data.name, created_at: new Date().toISOString() }]);
      toast.success(t("passkey_added") || "Passkey added successfully");
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error(t("passkey_cancelled") || "Passkey registration was cancelled");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to add passkey");
      }
    } finally {
      setRegisteringPasskey(false);
    }
  }, [API, authHeader, t]);

  const handleDeletePasskey = useCallback(async (passkeyId) => {
    try {
      await axios.delete(`${API}/auth/passkey/${passkeyId}`, { headers: authHeader() });
      setPasskeys((prev) => prev.filter((p) => p.id !== passkeyId));
      toast.success(t("passkey_deleted") || "Passkey removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete passkey");
    }
  }, [API, authHeader, t]);

  const isAdmin = user?.role === "admin";

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error(t("avatar_invalid_type") || "Only JPEG, PNG or WEBP images are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("avatar_too_large") || "Image must be under 5MB");
      return;
    }
    setAvatarPreview(URL.createObjectURL(file));
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/auth/profile/avatar`, formData, { headers: authHeader() });
      if (refreshUser) await refreshUser();
      toast.success(t("avatar_uploaded") || "Profile picture updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingAvatar(false);
      setAvatarPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await axios.delete(`${API}/auth/profile/avatar`, { headers: authHeader() });
      if (refreshUser) await refreshUser();
      toast.success(t("avatar_removed") || "Profile picture removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { mobile: profileMobile };
      if (isAdmin) {
        payload.name = profileName;
        payload.email = profileEmail;
      }
      await axios.put(`${API}/auth/profile`, payload, { headers: authHeader() });
      toast.success(t("profile_saved"));
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw !== confirmPw) { setError(t("passwords_no_match")); return; }
    if (newPw.length < 6) { setError(t("password_min_length")); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/change-my-password`,
        { current_password: currentPw, new_password: newPw },
        { headers: authHeader() }
      );
      toast.success(t("password_changed"));
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelegate = async () => {
    if (!delegateId) return;
    setDelegating(true);
    try {
      await axios.put(`${API}/auth/delegate`, { delegate_id: delegateId }, { headers: authHeader() });
      toast.success(t("delegation_set"));
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setDelegating(false); }
  };

  const handleRevoke = async () => {
    setDelegating(true);
    try {
      await axios.delete(`${API}/auth/delegate`, { headers: authHeader() });
      toast.success(t("delegation_revoked"));
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setDelegating(false); }
  };

  const avatarSrc = avatarPreview || resolveAvatarSrc(user?.profile_picture);
  const roleLabel = user?.role === "admin" ? t("admin_role") : user?.role === "finance_manager" ? t("finance_manager_role") : t("account_manager_role");

  const tabs = [
    { id: "personal", label: t("tab_personal_info") || "Personal Info", icon: User },
    { id: "security", label: t("tab_security") || "Security", icon: Key },
    ...(canDelegate ? [{ id: "delegation", label: t("tab_delegation") || "Delegation", icon: UserPlus }] : []),
    { id: "passkeys", label: t("tab_passkeys") || "Passkeys", icon: Fingerprint },
  ];

  const cardCls = "bg-white dark:bg-[var(--dm-card)] rounded-[24px] border-none shadow-[0_4px_20px_rgba(0,0,0,0.03),0_12px_36px_rgba(0,0,0,0.05)] dark:shadow-[var(--dm-card-shadow)]";

  return (
    <div data-testid="profile-page-container" className="h-full lg:h-[calc(100vh-80px)] overflow-y-auto lg:overflow-hidden p-4 sm:p-6 lg:p-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 h-full max-w-5xl mx-auto items-start">

        {/* Left: Identity card */}
        <div className={`lg:col-span-4 flex flex-col gap-4 h-full ${cardCls} p-6`} data-testid="profile-info-card">
          <div className="flex flex-col items-center text-center">
            <div data-testid="profile-avatar-container" className="relative w-24 h-24 mb-4">
              <div className="w-24 h-24 rounded-full bg-[#F3CF5B] flex items-center justify-center text-[#1C1D20] font-bold text-3xl overflow-hidden shadow-[0_0_0_4px_rgba(243,207,91,0.25)]">
                {avatarSrc ? (
                  <img data-testid="profile-avatar-img" src={avatarSrc} alt="" className="w-full h-full object-cover" />
                ) : (
                  user?.name?.[0]?.toUpperCase() || "U"
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
              <button
                data-testid="profile-avatar-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute bottom-0 end-0 w-8 h-8 rounded-full bg-[#1C1D20] dark:bg-[var(--dm-input-bg)] border-2 border-white dark:border-[var(--dm-card)] flex items-center justify-center text-white dark:text-[var(--dm-text)] hover:scale-105 transition-transform"
                title={t("change_photo") || "Change photo"}
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
              <input
                ref={fileInputRef}
                data-testid="profile-avatar-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarFile}
                className="hidden"
              />
            </div>
            {user?.profile_picture && (
              <button
                data-testid="profile-avatar-remove-btn"
                onClick={handleRemoveAvatar}
                className="text-[11px] text-[#E44E50] hover:underline mb-3 -mt-1"
              >
                {t("remove_photo") || "Remove photo"}
              </button>
            )}
            <p className="text-[10px] text-[#1C1D20]/40 dark:text-[var(--dm-placeholder)] -mt-1 mb-3">
              {t("avatar_specs") || "Max 5MB · JPEG, PNG or WEBP"}
            </p>

            <h2 className="text-lg font-semibold text-[#1C1D20] dark:text-[var(--dm-text)] tracking-tight">{user?.name}</h2>
            <span className={`mt-1.5 inline-block font-semibold text-xs px-2.5 py-1 rounded-full ${ROLE_BADGE[user?.role] || ROLE_BADGE.account_manager}`}>
              {roleLabel}
            </span>
            <p className="text-xs text-[#1C1D20]/60 dark:text-[var(--dm-muted-text)] mt-2 truncate max-w-full">{user?.email}</p>
          </div>

          <div className="border-t border-[#E5E5E5] dark:border-[var(--dm-input-border)] pt-4 mt-auto space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#1C1D20]/50 dark:text-[var(--dm-muted-text)]">{t("status") || "Status"}</span>
              <span className="flex items-center gap-1 text-[#1F7A45] dark:text-[#6FD39B] font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1F7A45] dark:bg-[#6FD39B]" />
                {t("active") || "Active"}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#1C1D20]/50 dark:text-[var(--dm-muted-text)]">{t("passkeys") || "Passkeys"}</span>
              <span className="text-[#1C1D20] dark:text-[var(--dm-text)] font-medium">{passkeys.length}</span>
            </div>
          </div>
        </div>

        {/* Right: Tabbed console */}
        <div className={`lg:col-span-8 flex flex-col gap-4 h-full ${cardCls} p-5 sm:p-6 overflow-y-auto lg:overflow-hidden`}>
          {/* Tabs */}
          <div className="flex items-center gap-1.5 flex-wrap border-b border-[#E5E5E5] dark:border-[var(--dm-input-border)] pb-3 flex-shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`profile-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-colors ${
                  activeTab === tab.id
                    ? "bg-[#1C1D20] text-white dark:bg-[var(--dm-btn-primary-bg)] dark:text-[var(--dm-btn-primary-text)]"
                    : "text-[#1C1D20]/60 dark:text-[var(--dm-muted-text)] hover:bg-[#F6F5F2] dark:hover:bg-[var(--dm-hover)]"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Personal Info */}
            {activeTab === "personal" && (
              <div className="space-y-4 max-w-md">
                <div>
                  <label className={labelCls}>
                    <User className="w-3 h-3 inline me-1" />{t("name")}
                    {!isAdmin && <span className="text-[10px] text-[#1C1D20]/30 dark:text-[var(--dm-placeholder)] ms-1 normal-case">({t("admin_only")})</span>}
                  </label>
                  {isAdmin ? (
                    <input data-testid="profile-name-input" value={profileName} onChange={(e) => setProfileName(e.target.value)} className={inputCls} />
                  ) : (
                    <p className="h-10 flex items-center px-3 rounded-lg bg-[#F6F5F2] dark:bg-[var(--dm-input-bg)] text-sm text-[#1C1D20] dark:text-[var(--dm-text)]">{user?.name}</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>
                    <Mail className="w-3 h-3 inline me-1" />{t("email")}
                    {!isAdmin && <span className="text-[10px] text-[#1C1D20]/30 dark:text-[var(--dm-placeholder)] ms-1 normal-case">({t("admin_only")})</span>}
                  </label>
                  {isAdmin ? (
                    <input data-testid="profile-email-input" value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} className={inputCls} />
                  ) : (
                    <p className="h-10 flex items-center px-3 rounded-lg bg-[#F6F5F2] dark:bg-[var(--dm-input-bg)] text-sm text-[#1C1D20] dark:text-[var(--dm-text)]">{user?.email}</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}><Phone className="w-3 h-3 inline me-1" />{t("mobile")}</label>
                  <input data-testid="profile-mobile-input" value={profileMobile} onChange={(e) => setProfileMobile(e.target.value)} placeholder={t("mobile_placeholder")} dir="ltr" className={inputCls} />
                </div>
                <div className="flex justify-end pt-1">
                  <button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={savingProfile} className={primaryBtnCls}>
                    {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {t("save_changes")}
                  </button>
                </div>
              </div>
            )}

            {/* Security */}
            {activeTab === "security" && (
              <form onSubmit={handleChangePassword} data-testid="profile-password-form" className="space-y-4 max-w-md">
                <div>
                  <label className={labelCls}>{t("current_password")}</label>
                  <input data-testid="current-password-input" type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} placeholder="••••••" className={inputCls} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>{t("new_password")}</label>
                    <input data-testid="new-password-input" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="••••••" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>{t("confirm_password")}</label>
                    <input data-testid="confirm-password-input" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="••••••" className={inputCls} />
                  </div>
                </div>
                {error && <p className="text-sm text-[#E44E50]" data-testid="profile-pw-error">{error}</p>}
                <div className="flex justify-end">
                  <button data-testid="save-password-btn" type="submit" disabled={saving} className={primaryBtnCls}>
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t("change_password_btn")}
                  </button>
                </div>
              </form>
            )}

            {/* Delegation */}
            {activeTab === "delegation" && canDelegate && (
              <div data-testid="delegation-section" className="max-w-md">
                <p className="text-xs text-[#1C1D20]/50 dark:text-[var(--dm-placeholder)] mb-4">{t("delegation_desc")}</p>
                {user?.delegated_to ? (
                  <div className="flex items-center gap-3 p-3 bg-[#6763A9]/5 border border-[#6763A9]/20 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[#6763A9] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {user.delegated_to_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1C1D20] dark:text-[var(--dm-text)]">{user.delegated_to_name}</p>
                      <p className="text-[11px] text-[#6763A9]">{t("currently_delegated")}</p>
                    </div>
                    <button data-testid="revoke-delegation-btn" disabled={delegating} onClick={handleRevoke}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E44E50]/10 text-[#E44E50] text-xs font-medium rounded-lg hover:bg-[#E44E50]/20 transition-colors disabled:opacity-60">
                      {delegating ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                      {t("revoke")}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <select data-testid="delegate-select" value={delegateId} onChange={(e) => setDelegateId(e.target.value)} className={`flex-1 ${inputCls}`}>
                      <option value="">{t("select_delegate")}</option>
                      {users.map((u2) => (
                        <option key={u2.id} value={u2.id}>{u2.name} ({u2.role === "admin" ? t("admin_role") : u2.role === "finance_manager" ? t("finance_manager_role") : t("account_manager_role")})</option>
                      ))}
                    </select>
                    <button data-testid="set-delegation-btn" disabled={!delegateId || delegating} onClick={handleDelegate} className={primaryBtnCls}>
                      {delegating && <Loader2 className="w-4 h-4 animate-spin" />}
                      {t("delegate_btn")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Passkeys */}
            {activeTab === "passkeys" && (
              <div className="max-w-md">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-[#1C1D20]/50 dark:text-[var(--dm-placeholder)] flex-1 pe-3">
                    {t("passkey_desc") || "Use fingerprint, Face ID, or security key to sign in without a password."}
                  </p>
                  <button data-testid="add-passkey-btn" onClick={handleAddPasskey} disabled={registeringPasskey}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors disabled:opacity-60 bg-[#F3CF5B] hover:bg-[#E5BF47] text-[#1C1D20] flex-shrink-0">
                    {registeringPasskey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Fingerprint className="w-3 h-3" />}
                    {t("add_passkey") || "Add Passkey"}
                  </button>
                </div>
                {passkeys.length === 0 ? (
                  <p className="text-xs text-[#1C1D20]/30 dark:text-[var(--dm-placeholder)] text-center py-6">
                    {t("no_passkeys") || "No passkeys registered yet"}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {passkeys.map((pk) => (
                      <div key={pk.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F6F5F2] dark:bg-[var(--dm-item-bg)]">
                        <div className="flex items-center gap-3">
                          <Fingerprint className="w-4 h-4 text-[#1C1D20]/40 dark:text-[var(--dm-placeholder)]" />
                          <div>
                            <p className="text-sm font-medium text-[#1C1D20] dark:text-[var(--dm-text)]">{pk.name}</p>
                            <p className="text-[10px] text-[#1C1D20]/40 dark:text-[var(--dm-placeholder)]">
                              {t("added") || "Added"} {new Date(pk.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button data-testid={`delete-passkey-${pk.id}`} onClick={() => handleDeletePasskey(pk.id)}
                          className="p-1.5 rounded-lg text-[#E44E50]/60 hover:text-[#E44E50] hover:bg-[#E44E50]/10 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
